/**
 * One (object, view) intersection of the visibility matrix: a single labelled,
 * colour-coded state chip and nothing else. Consumed by `VisibilityMatrix`,
 * which resolves `state` through `resolveVisibilityCellState` and owns the write
 * path — this component only forwards the row's whole value to the popover.
 * Clicking an editable chip opens `ViewVisibilityPopover` carrying the row's
 * full `ViewVisibilityControl`, highlighted on the clicked view, so the matrix
 * offers the identical control to every other visibility surface. A locked cell
 * — the `internal` column or the stored `hidden` sentinel — renders the chip
 * disabled with a tooltip saying why, and opens nothing; the tooltip mounts only
 * when it has a title, because an empty one costs a wrapper on every cell.
 * Labels and colours come from `visibilityChipVocabulary`, which the legend
 * samples too, so the two can never drift apart.
 */
import { FC } from 'react';
import { Button, Tooltip, Typography, theme } from 'antd';
import { ViewVisibilityPopover } from '../ViewVisibilityPopover';
import { visibilityChipVocabulary } from '../visibilityChipVocabulary';
import type { VisibilityMatrixCellProps } from './VisibilityMatrix.types';

const { Text } = Typography;

const HIDDEN_COLUMN_TOOLTIP =
    'This column is hidden outright: it never appears in any view. Nothing here can author or clear that setting.';

export const VisibilityMatrixCell: FC<VisibilityMatrixCellProps> = ({
    rowLabel,
    groupLabel,
    rowId,
    value,
    viewId,
    viewLabel,
    state,
    memberRows,
    objectKind,
    formTypeId,
    disabled,
    onChange,
}) => {
    const { token } = theme.useToken();
    const vocabulary = visibilityChipVocabulary(token);

    const objectLabel = `${groupLabel} — ${rowLabel} (${rowId})`;

    const chip = state.editable
        ? state.shown
            ? state.memberRows === 'none'
                ? vocabulary.shown
                : vocabulary[state.memberRows]
            : vocabulary.hidden
        : state.lock === 'internalView'
            ? vocabulary.alwaysShown
            : vocabulary.neverShown;

    const stateChip = (
        <Button
            size="small"
            disabled={disabled || !state.editable}
            aria-label={`${chip.label} in ${viewLabel}: ${objectLabel}`}
            style={{
                width: '100%',
                borderStyle: chip.dashed ? 'dashed' : 'solid',
                borderColor: chip.border,
                backgroundColor: chip.background,
                borderRadius: token.borderRadiusSM,
                paddingInline: token.paddingXXS,
            }}
        >
            <Text style={{ color: chip.color, fontSize: token.fontSizeSM }}>{chip.label}</Text>
        </Button>
    );

    return (
        <div
            role="cell"
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
        >
            {state.editable ? (
                <ViewVisibilityPopover
                    value={value}
                    onChange={onChange}
                    formTypeId={formTypeId}
                    objectLabel={rowLabel}
                    objectKind={objectKind}
                    contextLabels={[groupLabel]}
                    memberRows={memberRows}
                    highlightViewId={viewId}
                    matrixUnavailableReason="This cell is already in the matrix."
                    disabled={disabled}
                    aria-label={`Visibility in ${viewLabel}: ${objectLabel}`}
                >
                    {stateChip}
                </ViewVisibilityPopover>
            ) : (
                <Tooltip
                    title={
                        state.lock === 'internalView'
                            ? `${viewLabel} always shows every object.`
                            : HIDDEN_COLUMN_TOOLTIP
                    }
                >
                    <span>{stateChip}</span>
                </Tooltip>
            )}
        </div>
    );
};
